import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../styles/SellerForm.css';

const SellerForm = ({ onSellerAdded, onCancel }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    user_type: '',
    supervisor_scope: '',
    pos_id: ''
  });
  
  const [pointsOfSale, setPointsOfSale] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [userRole, setUserRole] = useState('');

  // Fetch points of sale and user role
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        
        // Obtener rol del usuario actual
        const userResponse = await axios.get('/api/auth/me', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        console.log('User response:', userResponse.data);
        setUserRole(userResponse.data.user.role);
        
        // Obtener todos los puntos de venta activos (no cerrados) del negocio del admin
        const posResponse = await axios.get('/api/points-of-sale/available', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        console.log('Puntos de venta disponibles:', posResponse.data);
        setPointsOfSale(posResponse.data);
      } catch (error) {
        console.error('Error fetching initial data:', error);
        setError('No se pudieron cargar los datos iniciales. Por favor, inténtelo de nuevo.');
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updatedFormData = {
      ...formData,
      [name]: value
    };
    
    // Auto-select "Supervisor General" when user type is "supervisor"
    if (name === 'user_type' && value === 'supervisor') {
      updatedFormData.supervisor_scope = 'general';
    }
    
    setFormData(updatedFormData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validaciones básicas
    if (!formData.email || !formData.password || !formData.full_name || !formData.user_type) {
      setError('Por favor, complete todos los campos obligatorios.');
      return;
    }
    
    // Validaciones específicas por tipo de usuario
    if (formData.user_type === 'seller' && !formData.pos_id) {
      setError('Por favor, seleccione un punto de venta para el vendedor.');
      return;
    }
    
    if (formData.user_type === 'supervisor') {
      if (!formData.supervisor_scope) {
        setError('Por favor, seleccione el alcance del supervisor.');
        return;
      }
      if (formData.supervisor_scope === 'pos_specific' && !formData.pos_id) {
        setError('Por favor, seleccione un punto de venta para el supervisor.');
        return;
      }
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      
      // Register the new user (seller or supervisor)
      const response = await axios.post('/api/sellers/register-seller', 
        {
          email: formData.email,
          password: formData.password,
          full_name: formData.full_name,
          user_type: formData.user_type,
          supervisor_scope: formData.supervisor_scope,
          pos_id: formData.pos_id
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      setSuccess(true);
      
      // Clear form after successful submission
      setFormData({
        email: '',
        password: '',
        full_name: '',
        user_type: '',
        supervisor_scope: '',
        pos_id: ''
      });
      
      // Notify parent component
      if (onSellerAdded) {
        onSellerAdded(response.data);
      }
      
      // Reset success message after 3 seconds
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
      
    } catch (error) {
      console.error('Error registering user:', error);
      setError(error.response?.data?.message || 'Error al registrar el usuario. Por favor, inténtelo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="seller-form-container">
      <h3>Registrar Nuevo Usuario</h3>
      
      {success && (
        <div className="success-message">
          ¡Usuario registrado exitosamente!
        </div>
      )}
      
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="seller-form" autoComplete="off">
        <div className="form-group">
          <label htmlFor="user_type">Tipo de Usuario</label>
          <select
            id="user_type"
            name="user_type"
            value={formData.user_type}
            onChange={handleChange}
            required
          >
            <option value="">Seleccionar tipo de usuario</option>
            <option value="seller">Vendedor</option>
            {userRole === 'admin' && (
              <option value="supervisor">Supervisor</option>
            )}
          </select>
        </div>
        
        <div className="form-group">
          <label htmlFor="full_name">Nombre Completo</label>
          <input
            type="text"
            id="full_name"
            name="full_name"
            value={formData.full_name}
            onChange={handleChange}
            placeholder={`Nombre completo del ${formData.user_type === 'supervisor' ? 'supervisor' : 'vendedor'}`}
            required
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="email">Correo Electrónico</label>
          <input
            type="text"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="correo@ejemplo.com"
            autoComplete="off"
            data-lpignore="true"
            pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
            title="Por favor ingrese un correo electrónico válido"
            required
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="password">Contraseña</label>
          <input
            type="password"
            id="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            placeholder="Contraseña"
            autoComplete="new-password"
            required
          />
        </div>
        
        {formData.user_type === 'supervisor' && (
          <div className="form-group">
            <label htmlFor="supervisor_scope">Alcance del Supervisor</label>
            <select
              id="supervisor_scope"
              name="supervisor_scope"
              value={formData.supervisor_scope}
              onChange={handleChange}
              disabled={true}
              required
            >
              <option value="general">Supervisor General (Todo el Negocio)</option>
            </select>
          </div>
        )}
        
        {((formData.user_type === 'seller') || 
          (formData.user_type === 'supervisor' && formData.supervisor_scope === 'pos_specific')) && (
          <div className="form-group">
            <label htmlFor="pos_id">Punto de Venta</label>
            <select
              id="pos_id"
              name="pos_id"
              value={formData.pos_id}
              onChange={handleChange}
              required
            >
              <option value="">Seleccionar punto de venta</option>
              {pointsOfSale.map(pos => (
                <option key={pos.id} value={pos.id}>
                  {pos.pos_name}
                </option>
              ))}
            </select>
          </div>
        )}
        
        <div className="form-actions">
          <button 
            type="button" 
            onClick={onCancel} 
            className="cancel-btn"
          >
            Cancelar
          </button>
          <button 
            type="submit" 
            className="submit-btn" 
            disabled={loading}
          >
            {loading ? 'Registrando...' : `Registrar ${formData.user_type === 'supervisor' ? 'Supervisor' : 'Vendedor'}`}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SellerForm;
